import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { STATUS_MAIN_SSO_TOKEN_STORAGE_KEY } from '../core/status-main-page.constants';
import {
  extractOrgSlugFromAliasResponse,
  getAliasCandidatesFromHost
} from '../core/status-main-page.url-resolver';
import {
  ManageSubscriptionResponse,
  ManageSubscriptionServiceGroup,
  ManageSubscriptionServiceItem,
  UpdateSubscriberServicesRequest
} from './status-main-page-manage-subscription.models';

type ManageViewState = 'loading' | 'loaded' | 'invalid' | 'error';

interface ManageSubscriptionServiceView extends ManageSubscriptionServiceItem {
  checked: boolean;
}

interface ManageSubscriptionServiceGroupView extends ManageSubscriptionServiceGroup {
  services: ManageSubscriptionServiceView[];
  selected: boolean;
  indeterminate: boolean;
  selectedCount: number;
  totalCount: number;
}

interface ManageRouteContext {
  org: string;
  slug: string;
}

@Component({
  selector: 'app-status-main-page-manage-subscription',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSnackBarModule
  ],
  templateUrl: './status-main-page-manage-subscription.component.html',
  styleUrls: [
    './status-main-page-manage-subscription.component.scss',
    './status-main-page-manage-subscription.component.preferences-actions.scss',
    './status-main-page-manage-subscription.component.modal-dark-responsive.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusMainPageManageSubscriptionComponent implements OnInit, OnDestroy {
  viewState: ManageViewState = 'loading';
  org = '';
  slug = '';
  token = '';
  isDarkTheme = false;

  model: ManageSubscriptionResponse | null = null;
  groupedServices: ManageSubscriptionServiceGroupView[] = [];
  subscribeToAllServices = true;
  selectedServiceIds: number[] = [];

  invalidMessage = 'This manage subscription link is invalid or expired. Please request a new link.';
  loadErrorMessage = '';
  actionErrorMessage = '';
  successMessage = '';
  selectionMessage = '';

  isSaving = false;
  isUnsubscribing = false;
  showUnsubscribeConfirm = false;

  private readonly destroy$ = new Subject<void>();
  private readonly themeStorageKey = 'alertops-status-main-theme';

  /**
   * Injects services used for routing, API calls, state management, and UI coordination.
   */
  constructor(
    private readonly route: ActivatedRoute,
    private readonly statusPageService: StatusPageService,
    private readonly snackBar: MatSnackBar,
    private readonly _cd: ChangeDetectorRef
  ) {}

  /**
   * Initializes component state, derives route context, and starts first-pass data loading.
   */
  ngOnInit(): void {
    this.initializeThemePreference();
    if (!this.isSupportedManageRoute()) {
      this.setInvalidState('This manage subscription link is invalid or unsupported. Please use the original email link.');
      return;
    }

    this.slug = this.resolveRouteSlug();
    this.token = String(this.route.snapshot.queryParamMap.get('token') || '').trim();

    if (!this.token) {
      this.setInvalidState('This manage subscription link is missing a token. Please use the original email link.');
      return;
    }

    this.resolveManageRouteContext()
      .pipe(
        finalize(() => this._cd.markForCheck()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (context) => {
          if (!context) {
            this.setInvalidState('This manage subscription link does not match a published status page.');
            return;
          }
          this.org = context.org;
          this.slug = context.slug;
          this.loadManageSubscription();
        },
        error: () => {
          this.setInvalidState('This manage subscription link does not match a published status page.');
        }
      });
  }

  /**
   * Returns theme toggle label for template bindings and conditional rendering.
   */
  get themeToggleLabel(): string {
    return this.isDarkTheme ? 'Switch to light mode' : 'Switch to dark mode';
  }

  /**
   * Disposes subscriptions when component is destroyed.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Returns whether actions should be disabled from the current state.
   */
  get actionsLocked(): boolean {
    return this.viewState !== 'loaded' || this.isSaving || this.isUnsubscribing;
  }

  /**
   * Returns whether selection controls should be disabled from the current state.
   */
  get selectionLocked(): boolean {
    if (this.actionsLocked) {
      return true;
    }
    if (this.subscribeToAllServices) {
      return true;
    }
    return this.model?.isActive === false;
  }

  /**
   * Returns whether save action should be disabled from the current state.
   */
  get saveDisabled(): boolean {
    if (this.actionsLocked || !this.model || this.model.isActive === false) {
      return true;
    }
    return !this.subscribeToAllServices && this.selectedServiceIds.length === 0;
  }

  /**
   * Returns whether unsubscribe action should be disabled from the current state.
   */
  get unsubscribeDisabled(): boolean {
    return this.actionsLocked || !this.model || this.model.isActive === false;
  }

  /**
   * Returns whether empty services should be shown from the current state.
   */
  get showNoServices(): boolean {
    return (this.model?.availableServices?.length || 0) === 0;
  }

  /**
   * Returns total services count from the current state.
   */
  get totalServiceCount(): number {
    return this.model?.availableServices?.length || 0;
  }

  /**
   * Returns subscriber contact from the current state.
   */
  get subscriberContact(): string {
    const address = String(this.model?.address || '').trim();
    if (address) {
      return address;
    }
    const fallbackEmail = String(this.model?.fallbackEmail || '').trim();
    return fallbackEmail || 'Unknown subscriber';
  }

  /**
   * Returns subscriber channel label from the current state.
   */
  get subscriberChannelLabel(): string {
    const code = Number(this.model?.subscriberType);
    if (!Number.isFinite(code)) {
      return 'Unknown';
    }

    switch (code) {
      case 1:
        return 'Email';
      case 2:
        return 'SMS';
      case 3:
        return 'Webhook';
      case 4:
        return 'Slack';
      case 5:
        return 'MS Teams';
      default:
        return `Type ${code}`;
    }
  }

  /**
   * Handles preference mode change from the UI.
   */
  onModeChanged(mode: 'all' | 'selected'): void {
    this.subscribeToAllServices = mode === 'all';
    this.selectionMessage = '';
    this.actionErrorMessage = '';
    this.successMessage = '';
    this._cd.markForCheck();
  }

  /**
   * Toggles theme based on user intent.
   */
  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;
    this.persistThemePreference();
    this._cd.markForCheck();
  }

  /**
   * Handles group checkbox change from the UI.
   */
  toggleGroup(group: ManageSubscriptionServiceGroupView, checked: boolean): void {
    if (this.selectionLocked) {
      return;
    }
    group.services.forEach((service) => {
      service.checked = checked;
    });
    this.afterSelectionChanged();
  }

  /**
   * Handles service checkbox change from the UI.
   */
  toggleService(group: ManageSubscriptionServiceGroupView, service: ManageSubscriptionServiceView, checked: boolean): void {
    if (this.selectionLocked) {
      return;
    }
    service.checked = checked;
    this.recalculateGroupSelectionState(group);
    this.afterSelectionChanged();
  }

  /**
   * Handles save from the UI.
   */
  onSave(): void {
    if (this.saveDisabled || !this.model) {
      if (!this.subscribeToAllServices && this.selectedServiceIds.length === 0) {
        this.selectionMessage = 'Select at least one service or choose All services.';
      }
      return;
    }

    this.selectionMessage = '';
    this.actionErrorMessage = '';
    this.successMessage = '';

    const payload: UpdateSubscriberServicesRequest = {
      subscribedToAllServices: this.subscribeToAllServices,
      serviceIds: this.subscribeToAllServices ? [] : [...this.selectedServiceIds]
    };

    this.isSaving = true;
    const ssoToken = this.getSsoTokenForRequest();
    this.statusPageService
      .updatePublicSubscriberManage(this.org, this.slug, this.token, payload, ssoToken)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this._cd.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          if (this.model) {
            this.model.subscribedToAllServices = this.subscribeToAllServices;
            this.model.subscribedServiceIds = payload.serviceIds;
            this.model.subscribedServiceCount = this.subscribeToAllServices
              ? this.totalServiceCount
              : payload.serviceIds.length;
          }
          this.isSaving = false;
          this._cd.markForCheck();
          this.successMessage = 'Preferences saved successfully.';
          this.actionErrorMessage = '';
        },
        error: (error: HttpErrorResponse) => {
          this.isSaving = false;
          this._cd.markForCheck();
          this.actionErrorMessage = this.resolveApiError(
            error,
            'Unable to save preferences. Please try again.'
          );
        }
      });
  }

  /**
   * Handles unsubscribe from the UI.
   */
  onUnsubscribe(): void {
    if (this.unsubscribeDisabled || !this.model) {
      return;
    }
    this.showUnsubscribeConfirm = true;
    this._cd.markForCheck();
  }

  /**
   * Closes unsubscribe confirmation modal from the UI.
   */
  closeUnsubscribeConfirm(): void {
    if (this.isUnsubscribing) {
      return;
    }
    this.showUnsubscribeConfirm = false;
    this._cd.markForCheck();
  }

  /**
   * Confirms unsubscribe intent from the UI.
   */
  confirmUnsubscribe(): void {
    if (this.isUnsubscribing) {
      return;
    }
    this.showUnsubscribeConfirm = false;
    this.executeUnsubscribe();
  }

  /**
   * Executes unsubscribe after explicit user confirmation.
   */
  private executeUnsubscribe(): void {
    if (this.unsubscribeDisabled || !this.model) {
      return;
    }

    this.selectionMessage = '';
    this.actionErrorMessage = '';
    this.successMessage = '';

    this.isUnsubscribing = true;
    const ssoToken = this.getSsoTokenForRequest();
    this.statusPageService
      .unsubscribePublicStatusPage(this.org, this.slug, this.token, ssoToken)
      .pipe(
        finalize(() => {
          this.isUnsubscribing = false;
          this._cd.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response: any) => {
          if (response?.success === false) {
            this.actionErrorMessage = response?.errorMessage || response?.message || 'Unable to unsubscribe right now. Please try again.';
            return;
          }
          if (this.model) {
            this.model.isActive = false;
          }
          this.successMessage = 'You have been unsubscribed from status page updates.';
          this.actionErrorMessage = '';
          this.snackBar.open('You have been unsubscribed.', '', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom'
          });
        },
        error: (error: HttpErrorResponse) => {
          this.actionErrorMessage = this.resolveApiError(
            error,
            'Unable to unsubscribe right now. Please try again.'
          );
        }
      });
  }

  /**
   * Handles retry from the UI.
   */
  onRetryLoad(): void {
    this.loadManageSubscription();
  }

  /**
   * Returns stable group identity for template tracking.
   */
  trackByGroup(index: number, group: ManageSubscriptionServiceGroupView): number | string {
    return group.groupId ?? group.groupName ?? index;
  }

  /**
   * Returns stable service identity for template tracking.
   */
  trackByService(index: number, service: ManageSubscriptionServiceView): number {
    return service.serviceId || index;
  }

  /**
   * Loads manage subscription and applies it to local component state.
   */
  private loadManageSubscription(): void {
    this.viewState = 'loading';
    this.loadErrorMessage = '';
    this.successMessage = '';
    this.actionErrorMessage = '';
    this.selectionMessage = '';
    this.showUnsubscribeConfirm = false;

    this.statusPageService
      .getPublicSubscriberManage(this.org, this.slug, this.token, this.getSsoTokenForRequest())
      .pipe(
        finalize(() => {
          this._cd.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (response) => {
          const normalized = this.normalizeResponse(response);
          if (!normalized) {
            this.setInvalidState();
            return;
          }

          if (this.hasSlugMismatch(normalized.statusPageSlug)) {
            this.setInvalidState('This manage link does not match the status page in the URL.');
            return;
          }

          this.model = normalized;
          this.subscribeToAllServices = normalized.subscribedToAllServices;
          this.initializeServiceSelection(normalized);
          this.viewState = 'loaded';
        },
        error: (error: HttpErrorResponse) => {
          if (error?.status === 400 || error?.status === 404) {
            this.setInvalidState();
            return;
          }
          this.viewState = 'error';
          this.loadErrorMessage = this.resolveApiError(
            error,
            'Unable to load subscription preferences. Please retry in a moment.'
          );
        }
      });
  }

  /**
   * Initializes grouped selection state from backend response.
   */
  private initializeServiceSelection(response: ManageSubscriptionResponse): void {
    const selectedIds = this.resolveInitialSelectedServiceIds(response);
    this.groupedServices = this.buildGroupedServices(response.availableServices || [], selectedIds);
    this.selectedServiceIds = this.collectSelectedServiceIds();
  }

  /**
   * Builds grouped service view model from service items.
   */
  private buildGroupedServices(
    services: ManageSubscriptionServiceItem[],
    selectedIds: Set<number>
  ): ManageSubscriptionServiceGroupView[] {
    const groups = new Map<string, ManageSubscriptionServiceGroupView>();

    (services || []).forEach((service) => {
      const serviceId = Number(service?.serviceId);
      if (!Number.isFinite(serviceId) || serviceId <= 0) {
        return;
      }

      const groupId = this.parseNullableNumber(service?.groupId);
      const groupName = String(service?.groupName || '').trim() || 'Other Services';
      const key = `${groupId ?? 'other'}::${groupName.toLowerCase()}`;

      if (!groups.has(key)) {
        groups.set(key, {
          groupId,
          groupName,
          services: [],
          selected: false,
          indeterminate: false,
          selectedCount: 0,
          totalCount: 0
        });
      }

      const target = groups.get(key)!;
      target.services.push({
        ...service,
        serviceId,
        checked: selectedIds.has(serviceId)
      });
    });

    const result = Array.from(groups.values());
    result.forEach((group) => this.recalculateGroupSelectionState(group));
    return result;
  }

  /**
   * Recomputes selected service ids and validation state after selection updates.
   */
  private afterSelectionChanged(): void {
    this.groupedServices.forEach((group) => this.recalculateGroupSelectionState(group));
    this.selectedServiceIds = this.collectSelectedServiceIds();
    if (this.selectedServiceIds.length > 0) {
      this.selectionMessage = '';
    }
    this.successMessage = '';
    this.actionErrorMessage = '';
    this._cd.markForCheck();
  }

  /**
   * Recalculates group aggregate selection state.
   */
  private recalculateGroupSelectionState(group: ManageSubscriptionServiceGroupView): void {
    const selectedCount = group.services.filter((service) => service.checked).length;
    group.selectedCount = selectedCount;
    group.totalCount = group.services.length;
    group.selected = selectedCount > 0 && selectedCount === group.totalCount;
    group.indeterminate = selectedCount > 0 && selectedCount < group.totalCount;
  }

  /**
   * Collects selected service ids from grouped state.
   */
  private collectSelectedServiceIds(): number[] {
    const ids = new Set<number>();
    this.groupedServices.forEach((group) => {
      group.services.forEach((service) => {
        if (service.checked) {
          ids.add(service.serviceId);
        }
      });
    });
    return Array.from(ids);
  }

  /**
   * Resolves initial selected ids based on response and fallback rules.
   */
  private resolveInitialSelectedServiceIds(response: ManageSubscriptionResponse): Set<number> {
    const subscribedIds = Array.isArray(response?.subscribedServiceIds)
      ? response.subscribedServiceIds
      : [];

    if (subscribedIds.length) {
      return new Set(
        subscribedIds
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      );
    }

    return new Set(
      (response?.availableServices || [])
        .filter((item) => !!item?.isSubscribed)
        .map((item) => Number(item?.serviceId))
        .filter((value) => Number.isFinite(value) && value > 0)
    );
  }

  /**
   * Normalizes API response before UI binding.
   */
  private normalizeResponse(response: ManageSubscriptionResponse | null | undefined): ManageSubscriptionResponse | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const availableServices = Array.isArray(response.availableServices)
      ? response.availableServices
          .map((service): ManageSubscriptionServiceItem | null => {
            const serviceId = Number(service?.serviceId);
            if (!Number.isFinite(serviceId) || serviceId <= 0) {
              return null;
            }

            return {
              serviceId,
              name: String(service?.name || `Service ${serviceId}`),
              description: String(service?.description || ''),
              groupId: this.parseNullableNumber(service?.groupId),
              groupName: service?.groupName ? String(service.groupName) : null,
              isSubscribed: service?.isSubscribed === true
            };
          })
          .filter((service): service is ManageSubscriptionServiceItem => !!service)
      : [];

    return {
      ...response,
      statusPageName: String(response.statusPageName || 'Status Page'),
      statusPageSlug: String(response.statusPageSlug || ''),
      address: String(response.address || ''),
      fallbackEmail: response.fallbackEmail ? String(response.fallbackEmail) : null,
      subscriberType: Number(response.subscriberType) || 0,
      status: Number(response.status) || 0,
      isVerified: response.isVerified === true,
      subscribedToAllServices: response.subscribedToAllServices === true,
      isActive: response.isActive !== false,
      verifiedDate: response.verifiedDate ? String(response.verifiedDate) : null,
      subscriberId: Number(response.subscriberId) || 0,
      statusPageId: Number(response.statusPageId) || 0,
      userId: response.userId === null || response.userId === undefined ? null : Number(response.userId),
      subscribedServiceIds: Array.isArray(response.subscribedServiceIds)
        ? response.subscribedServiceIds
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        : [],
      subscribedServiceCount: Number(response.subscribedServiceCount) || 0,
      availableServices
    };
  }

  /**
   * Sets invalid state with an optional message override.
   */
  private setInvalidState(message?: string): void {
    this.viewState = 'invalid';
    this.model = null;
    this.groupedServices = [];
    this.selectedServiceIds = [];
    this.invalidMessage = message || 'This manage subscription link is invalid or expired. Please request a new link.';
  }

  /**
   * Returns true when route slug and response slug mismatch.
   */
  private hasSlugMismatch(responseSlug: string): boolean {
    const routeSlug = String(this.slug || '').trim().toLowerCase();
    const normalizedResponseSlug = String(responseSlug || '').trim().toLowerCase();

    if (!routeSlug || !normalizedResponseSlug) {
      return false;
    }

    return routeSlug !== normalizedResponseSlug;
  }

  /**
   * Resolves route slug from the matched route.
   */
  private resolveRouteSlug(): string {
    return String(this.route.snapshot.paramMap.get('slug') || '').trim();
  }

  /**
   * Resolves account and slug context before calling scoped manage APIs.
   */
  private resolveManageRouteContext(): Observable<ManageRouteContext | null> {
    const routeContext = this.resolveManageContextFromRouteParams();
    if (routeContext) {
      return of(routeContext);
    }

    if (this.slug) {
      return this.resolveManageContextFromAlias(this.slug, this.slug);
    }

    return this.resolveManageContextFromCurrentHost();
  }

  /**
   * Resolves context from explicit app-host manage routes.
   */
  private resolveManageContextFromRouteParams(): ManageRouteContext | null {
    const org = String(
      this.route.snapshot.paramMap.get('account') ||
      this.route.snapshot.paramMap.get('org') ||
      ''
    ).trim();
    const slug = this.resolveRouteSlug();

    if (!org || !slug) {
      return null;
    }

    return { org, slug };
  }

  /**
   * Resolves context from custom-domain aliases.
   */
  private resolveManageContextFromCurrentHost(): Observable<ManageRouteContext | null> {
    const aliases = getAliasCandidatesFromHost(this.getCurrentHost());
    return this.resolveManageContextFromAliasCandidates(aliases);
  }

  /**
   * Returns current host from the browser context.
   */
  private getCurrentHost(): string {
    const win = this.getWindow();
    if (!win?.location?.hostname) {
      return '';
    }
    return String(win.location.hostname || '').trim().toLowerCase();
  }

  /**
   * Resolves context from the first alias that maps to a status page.
   */
  private resolveManageContextFromAliasCandidates(
    aliases: string[],
    index = 0
  ): Observable<ManageRouteContext | null> {
    const alias = String(aliases[index] || '').trim();
    if (!alias) {
      return of(null);
    }

    return this.resolveManageContextFromAlias(alias).pipe(
      switchMap((context) => context
        ? of(context)
        : this.resolveManageContextFromAliasCandidates(aliases, index + 1)
      )
    );
  }

  /**
   * Resolves context for a single alias value.
   */
  private resolveManageContextFromAlias(alias: string, expectedSlug?: string): Observable<ManageRouteContext | null> {
    const normalizedAlias = String(alias || '').trim();
    if (!normalizedAlias) {
      return of(null);
    }

    return this.statusPageService
      .getPublicStatusPageByAlias(normalizedAlias, this.getSsoTokenForRequest())
      .pipe(
        map((response) => this.extractManageRouteContext(response, expectedSlug)),
        catchError(() => of(null))
      );
  }

  /**
   * Extracts route context from alias response payloads.
   */
  private extractManageRouteContext(response: any, expectedSlug?: string): ManageRouteContext | null {
    const resolved = extractOrgSlugFromAliasResponse(response);
    const org = String(resolved?.org || '').trim();
    const slug = String(resolved?.slug || '').trim();
    const expected = String(expectedSlug || '').trim().toLowerCase();

    if (!org || !slug) {
      return null;
    }
    if (expected && slug.toLowerCase() !== expected) {
      return null;
    }

    return { org, slug };
  }

  /**
   * Returns true when the router matched a canonical manage-link route.
   */
  private isSupportedManageRoute(): boolean {
    return this.route.snapshot.data?.['manageLinkSupported'] === true;
  }

  /**
   * Parses nullable number values safely.
   */
  private parseNullableNumber(value: any): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Resolves API error message with fallback text.
   */
  private resolveApiError(error: HttpErrorResponse, fallback: string): string {
    return String(
      error?.error?.errorMessage ||
      error?.error?.message ||
      error?.message ||
      fallback
    ).trim() || fallback;
  }

  /**
   * Initializes theme preference before rendering.
   */
  private initializeThemePreference(): void {
    const win = this.getWindow();
    if (!win) {
      this.isDarkTheme = false;
      return;
    }

    try {
      const storedPreference = win.localStorage.getItem(this.themeStorageKey);
      if (storedPreference === 'dark' || storedPreference === 'light') {
        this.isDarkTheme = storedPreference === 'dark';
        return;
      }

      this.isDarkTheme = typeof win.matchMedia === 'function'
        ? win.matchMedia('(prefers-color-scheme: dark)').matches
        : false;
    } catch {
      this.isDarkTheme = false;
    }
  }

  /**
   * Persists theme preference in browser storage when available.
   */
  private persistThemePreference(): void {
    const win = this.getWindow();
    if (!win) {
      return;
    }

    try {
      win.localStorage.setItem(this.themeStorageKey, this.isDarkTheme ? 'dark' : 'light');
    } catch {
      // Ignore storage failures in restricted browser contexts.
    }
  }

  /**
   * Returns window when available.
   */
  private getWindow(): Window | null {
    if (typeof globalThis === 'undefined') {
      return null;
    }
    return (globalThis as { window?: Window }).window ?? null;
  }

  /**
   * Returns sso token for request from local storage.
   */
  private getSsoTokenForRequest(): string {
    const win = this.getWindow();
    if (!win) {
      return '';
    }
    try {
      return String(win.localStorage.getItem(STATUS_MAIN_SSO_TOKEN_STORAGE_KEY) || '').trim();
    } catch {
      return '';
    }
  }
}
